import { useEffect, useRef } from 'react'
import { type ToastT, useSonner } from 'sonner'

import { reactNodeToText } from '@/lib/react-node-text'
import { type ToastHistoryType, useToastHistoryStore } from '@/store/useToastHistoryStore'

const HISTORY_TYPES: readonly ToastHistoryType[] = ['success', 'error', 'info', 'warning', 'loading']

const toHistoryType = (type: ToastT['type']): ToastHistoryType =>
  HISTORY_TYPES.find((value) => value === type) ?? 'default'

const resolveText = (value: ToastT['title'] | ToastT['description']): string =>
  reactNodeToText(typeof value === 'function' ? value() : value).trim()

/** 表示されたトーストを履歴ストアに記録する。画面には何も描かない */
export const ToastHistoryRecorder = () => {
  const { toasts } = useSonner()
  const upsert = useToastHistoryStore((state) => state.upsert)
  // sonner の id はページ読み込みごとに 1 から振り直されるため、履歴用の id に対応付ける
  const recordedRef = useRef(new Map<ToastT['id'], { historyId: string; signature: string }>())

  useEffect(() => {
    for (const toast of toasts) {
      const type = toHistoryType(toast.type)
      const title = resolveText(toast.title)
      const description = resolveText(toast.description)
      if (!title && !description) continue

      const signature = `${type}\n${title}\n${description}`
      const recorded = recordedRef.current.get(toast.id)
      if (recorded?.signature === signature) continue

      const historyId = recorded?.historyId ?? `${Date.now()}-${toast.id}`
      recordedRef.current.set(toast.id, { historyId, signature })
      upsert({ id: historyId, type, title, description })
    }
  }, [toasts, upsert])

  return null
}
